import React, {
  RefObject,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styled from "@emotion/styled";
import { Caption1, colors, SubBody } from "@utils/ui_palette";
import { rgba } from "emotion-rgba";

export type AlertModalProps = {
  title: string;
  succeseText?: string;
  subTitle?: string;
  cancel?: boolean;
  data?: any;
  succeseCallback?: (data: any) => any;
  cancelCallback?: () => any;
};

export type AlertModalRef = {
  show: (props: AlertModalProps) => void;
  hide: () => void;
};

export class Alerts {
  static ref: RefObject<AlertModalRef>;
  static setRef = (ref: any) => {
    this.ref = ref;
  };

  static show = (props: AlertModalProps) => {
    this.ref.current?.show?.(props);
  };

  static hide = () => {
    this.ref.current?.hide?.();
  };
}

export const AlertModal = forwardRef((_props: any, _ref: any) => {
  const [isVisable, setVisable] = useState<boolean>(false);
  const [props, setProps] = useState<AlertModalProps>();

  const alertModalRef = useRef<AlertModalRef>();

  useLayoutEffect(() => {
    Alerts.setRef(alertModalRef);
  }, []);

  const handleSuccese = useCallback(() => {
    if (props && props.succeseCallback) {
      const propsData = props.data || null;
      props.succeseCallback(propsData);

      Alerts.hide();
    } else {
      Alerts.hide();
    }
  }, [props]);

  const handleCancel = useCallback(() => {
    if (props && props.cancelCallback) {
      props.cancelCallback();

      Alerts.hide();
    } else {
      Alerts.hide();
    }
  }, [props]);

  useImperativeHandle(alertModalRef, () => ({
    show: (params) => {
      setProps(params);
      setVisable(true);
    },
    hide: () => {
      setVisable(false);
    },
  }));

  return (
    <ReactNativeModal isVisable={isVisable}>
      {props && (
        <ModalBody>
          <Message>
            <SubBody>{props.title}</SubBody>

            {props.subTitle && props.subTitle !== "" && (
              <Caption1 marginTop={12} color={colors.Gray50}>
                {props.subTitle}
              </Caption1>
            )}
          </Message>

          {!props.cancel && (
            <Button onClick={handleSuccese}>
              <SubBody color={colors.White}>
                {props.succeseText ? props.succeseText : "OK"}
              </SubBody>
            </Button>
          )}

          {props.cancel && (
            <ButtonGroup>
              <CancelButton onClick={handleCancel}>
                <SubBody color={colors.Gray70}>Cancel</SubBody>
              </CancelButton>
              <OKButton onClick={handleSuccese}>
                <SubBody color={colors.White}>
                  {props.succeseText ? props.succeseText : "OK"}
                </SubBody>
              </OKButton>
            </ButtonGroup>
          )}
        </ModalBody>
      )}
    </ReactNativeModal>
  );
});

const ReactNativeModal = styled.div<{ isVisable: boolean }>`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  background-color: ${rgba(colors.Black, 0.5)};
  display: ${(props) => (props.isVisable ? "flex" : "none")};
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  width: 400px;
  background-color: ${colors.White};
  border-radius: 10px;
  min-height: 240px;
  padding: 30px;
  display: flex;
  flex-direction: column;
`;

const Message = styled.div`
  padding-top: 40px;
`;

const Button = styled.button`
  width: 100%;
  height: 52px;
  background-color: ${colors.Primary50};
  border-radius: 10px;
  border: 0;
  margin-top: auto;
  cursor: pointer;
`;

const OKButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  background-color: ${colors.Primary50};
  border-radius: 10px;
  width: 124px;
  cursor: pointer;
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 124px;
  height: 52px;
  background-color: ${colors.White};
  border: 1px solid ${colors.Gray30};
  border-radius: 10px;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export default AlertModal;
